import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowRight, Check, X, Heart, MessageSquare, Home, Users, Sparkles, Clock,
  Play, Star, Zap, Shield, ChevronRight, MapPin, Euro, Building2, TrendingUp,
  CheckCircle, Award, Timer, Phone
} from 'lucide-react';

const HowItWorksPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [swipeCount, setSwipeCount] = useState(247);

  // Animate swipe counter
  useEffect(() => {
    const interval = setInterval(() => {
      setSwipeCount(prev => prev + Math.floor(Math.random() * 2) + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = language === 'nl' ? [
    {
      quote: "Na 2 weken zoeken kreeg ik mijn eerste bezichtiging. Makkelijker dan zelf alles mailen!",
      name: "Fleur de V.",
      role: "Student, Amsterdam",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100",
      rating: 5
    },
    {
      quote: "Als starter op de woningmarkt is dit echt een uitkomst. Geen gedoe met standaard reacties.",
      name: "Daan K.",
      role: "Starter, Rotterdam", 
      image: "https://images.unsplash.com/photo-1628619487925-e9b8fc4c6b08?w=100",
      rating: 5
    },
    {
      quote: "Fijn platform om snel te zien wat beschikbaar is. De swipe functie maakt het makkelijk.",
      name: "Lisa M.",
      role: "Young professional, Utrecht",
      image: "https://images.unsplash.com/photo-1709202967828-e1a7823ccdf6?w=100",
      rating: 5
    }
  ] : [
    {
      quote: "After 2 weeks of searching I got my first viewing. Much easier than emailing everything myself!",
      name: "Fleur de V.",
      role: "Student, Amsterdam",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100",
      rating: 5
    },
    {
      quote: "As a starter on the housing market, this is really a lifesaver. No hassle with standard responses.",
      name: "Daan K.",
      role: "Starter, Rotterdam", 
      image: "https://images.unsplash.com/photo-1628619487925-e9b8fc4c6b08?w=100",
      rating: 5
    },
    {
      quote: "Great platform to quickly see what's available. The swipe feature makes it easy.",
      name: "Lisa M.",
      role: "Young professional, Utrecht",
      image: "https://images.unsplash.com/photo-1709202967828-e1a7823ccdf6?w=100",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#0D2137]" data-testid="how-it-works-page">
      <Header />
      
      <main className="flex-1 pt-16">
        {/* HERO - Ultra Clickbait with Video-like Feel */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137]"></div>
            <div className="absolute top-0 left-0 w-full h-full opacity-30">
              <div className="absolute top-20 left-[10%] w-96 h-96 bg-[#2E7D32] rounded-full blur-[120px] animate-pulse"></div>
              <div className="absolute bottom-20 right-[10%] w-[500px] h-[500px] bg-[#2E7D32]/50 rounded-full blur-[150px] animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
            
            {/* Floating property images in background */}
            <div className="absolute inset-0 overflow-hidden opacity-20">
              <img src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800" alt="" className="absolute top-10 left-[5%] w-64 h-48 object-cover rounded-2xl rotate-[-5deg] animate-float" />
              <img src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800" alt="" className="absolute top-40 right-[8%] w-56 h-40 object-cover rounded-2xl rotate-[8deg] animate-float" style={{animationDelay: '0.5s'}} />
              <img src="https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800" alt="" className="absolute bottom-32 left-[15%] w-48 h-36 object-cover rounded-2xl rotate-[5deg] animate-float" style={{animationDelay: '1s'}} />
            </div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left - Content */}
              <div className="text-center lg:text-left">
                {/* Live counter badge */}
                <div className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
                  <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
                  <span>{swipeCount.toLocaleString()} {language === 'nl' ? 'swipes vandaag' : 'swipes today'}</span>
                </div>

                <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-[0.9] mb-6">
                  {language === 'nl' ? 'Stop met' : 'Stop'}
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2E7D32] to-[#4CAF50]">
                    {language === 'nl' ? 'zoeken.' : 'searching.'}
                  </span>
                  <br />
                  {language === 'nl' ? 'Start met' : 'Start'}
                  <br />
                  <span className="relative inline-block">
                    {language === 'nl' ? 'swipen.' : 'swiping.'}
                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                      <path d="M2 10C40 2 80 2 100 6C140 10 180 4 198 8" stroke="#2E7D32" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                  </span>
                </h1>

                <p className="text-xl md:text-2xl text-white/60 mb-8 max-w-lg mx-auto lg:mx-0">
                  {language === 'nl' 
                    ? <>82% vindt binnen <span className="text-white font-bold">2 weken</span> een woning. Jij kunt de volgende zijn.</>
                    : <>82% find a home within <span className="text-white font-bold">2 weeks</span>. You could be next.</>
                  }
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button 
                    onClick={() => navigate('/swipematch')}
                    className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-16 px-10 text-xl font-black rounded-full shadow-2xl shadow-[#2E7D32]/30 group"
                  >
                    {language === 'nl' ? 'Start nu gratis' : 'Start free now'}
                    <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                    className="border-2 border-white/30 text-white hover:bg-white/10 h-16 px-8 text-lg font-bold rounded-full"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    {language === 'nl' ? 'Bekijk hoe het werkt' : 'See how it works'}
                  </Button>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-white/50 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#2E7D32]" />
                    <span>{language === 'nl' ? 'Gratis swipen' : 'Free swipes'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#2E7D32]" />
                    <span>{language === 'nl' ? '100% veilig' : '100% safe'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#2E7D32]" />
                    <span>{language === 'nl' ? 'Direct contact' : 'Direct contact'}</span>
                  </div>
                </div>
              </div>

              {/* Right - Visual Demo */}
              <div className="relative flex justify-center lg:justify-end">
                {/* Main phone mockup with real swipe interface */}
                <div className="relative">
                  <div className="bg-gradient-to-b from-[#1a1a2e] to-[#16213e] rounded-[3rem] p-3 shadow-2xl w-[280px] md:w-[320px]">
                    <div className="bg-white rounded-[2.5rem] overflow-hidden aspect-[9/18]">
                      {/* Status bar */}
                      <div className="bg-[#1F3A5F] px-6 py-3 flex items-center justify-between">
                        <span className="text-white/60 text-xs">9:41</span>
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 bg-[#2E7D32] rounded-full flex items-center justify-center">
                            <span className="text-white text-[8px] font-bold">3</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Property card */}
                      <div className="relative">
                        <img 
                          src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600" 
                          alt={language === 'nl' ? 'Woning' : 'Property'} 
                          className="w-full h-52 object-cover"
                        />
                        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="text-xs font-bold text-[#2E7D32]">{language === 'nl' ? 'NIEUW' : 'NEW'}</span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-white font-black text-2xl">€875</p>
                              <p className="text-white/80 text-xs">{language === 'nl' ? 'per week' : 'per week'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-bold text-sm">Amsterdam</p>
                              <p className="text-white/70 text-xs">Centrum • Studio</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Property details */}
                      <div className="p-4">
                        <div className="flex items-center gap-4 mb-4 text-sm text-[#9E9E9E]">
                          <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> 35m²</span>
                          <span className="flex items-center gap-1"><Users className="w-4 h-4" /> 1 {language === 'nl' ? 'pers' : 'pers'}</span>
                          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-[#2E7D32]" /> {language === 'nl' ? 'Geverifieerd' : 'Verified'}</span>
                        </div>
                        
                        {/* Swipe buttons */}
                        <div className="flex justify-center gap-6">
                          <button className="w-16 h-16 bg-white border-2 border-[#C04A4A] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                            <X className="w-8 h-8 text-[#C04A4A]" />
                          </button>
                          <button className="w-16 h-16 bg-[#2E7D32] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                            <Heart className="w-8 h-8 text-white" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating elements */}
                  <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-3 animate-bounce">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#2E7D32] rounded-full flex items-center justify-center">
                        <Heart className="w-4 h-4 text-white fill-white" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#2A2A2A]">Match!</p>
                        <p className="text-[10px] text-[#9E9E9E]">{language === 'nl' ? 'Nu chatten' : 'Chat now'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-4 -left-4 bg-[#2E7D32] text-white rounded-2xl shadow-xl p-3">
                    <div className="flex items-center gap-2">
                      <Timer className="w-5 h-5" />
                      <div>
                        <p className="text-xs font-bold">{language === 'nl' ? 'Gem. 2 weken' : 'Avg. 2 weeks'}</p>
                        <p className="text-[10px] text-white/70">{language === 'nl' ? 'tot je nieuwe woning' : 'to your new home'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 animate-bounce">
            <ChevronRight className="w-8 h-8 rotate-90" />
          </div>
        </section>

        {/* HOW IT WORKS - Visual Steps */}
        <section id="how-it-works" className="py-20 md:py-32 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Play className="w-4 h-4" />
                {language === 'nl' ? 'ZO WERKT HET' : 'HOW IT WORKS'}
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-[#2A2A2A] mb-4">
                {language === 'nl' ? 'Van aanmelden tot' : 'From sign up to'}<br/><span className="text-[#2E7D32]">{language === 'nl' ? 'sleuteloverdracht' : 'key handover'}</span>
              </h2>
              <p className="text-xl text-[#9E9E9E] max-w-2xl mx-auto">
                {language === 'nl' ? 'In 4 simpele stappen naar je nieuwe woning. Geen gedoe, geen stress.' : 'Find your new home in 4 simple steps. No hassle, no stress.'}
              </p>
            </div>

            {/* Step 1 */}
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-24">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                    1
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">{language === 'nl' ? 'Stap 1' : 'Step 1'}</p>
                    <h3 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Maak je profiel' : 'Create your profile'}</h3>
                  </div>
                </div>
                <p className="text-lg text-[#9E9E9E] mb-6 leading-relaxed">
                  {language === 'nl' 
                    ? <>In slechts <span className="text-[#2A2A2A] font-bold">30 seconden</span> vertel je ons wie je bent. Student, starter, expat of professional? Selecteer je steden en budget. Klaar!</>
                    : <>In just <span className="text-[#2A2A2A] font-bold">30 seconds</span> you tell us who you are. Student, starter, expat or professional? Select your cities and budget. Done!</>
                  }
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: '🎓', label: 'Student' },
                    { icon: '💼', label: 'Starter' },
                    { icon: '🌍', label: 'Expat' },
                    { icon: '👔', label: 'Professional' }
                  ].map((type, i) => (
                    <div key={i} className={`p-4 rounded-xl border-2 ${i === 0 ? 'border-[#2E7D32] bg-[#2E7D32]/5' : 'border-[#E5E5E5]'} flex items-center gap-3`}>
                      <span className="text-2xl">{type.icon}</span>
                      <span className="font-bold text-[#2A2A2A]">{type.label}</span>
                      {i === 0 && <Check className="w-5 h-5 text-[#2E7D32] ml-auto" />}
                    </div>
                  ))}
                </div>

                <div className="bg-[#F7F7F7] rounded-xl p-4 flex items-center gap-3">
                  <Timer className="w-6 h-6 text-[#2E7D32]" />
                  <span className="text-[#9E9E9E]">{language === 'nl' ? 'Duurt gemiddeld' : 'Takes on average'} <span className="text-[#2A2A2A] font-bold">30 {language === 'nl' ? 'seconden' : 'seconds'}</span></span>
                </div>
              </div>

              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600" 
                    alt={language === 'nl' ? 'Profiel aanmaken' : 'Create profile'} 
                    className="w-full max-w-md rounded-3xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-[#2E7D32]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Profiel compleet!' : 'Profile complete!'}</p>
                        <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Klaar om te swipen' : 'Ready to swipe'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-24">
              <div className="flex justify-center">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600" 
                    alt={language === 'nl' ? 'Swipe woningen' : 'Swipe properties'} 
                    className="w-full max-w-md rounded-3xl shadow-2xl"
                  />
                  {/* Swipe overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex gap-8">
                      <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl border-4 border-[#C04A4A]">
                        <X className="w-10 h-10 text-[#C04A4A]" />
                      </div>
                      <div className="w-20 h-20 bg-[#2E7D32] rounded-full flex items-center justify-center shadow-xl animate-pulse">
                        <Heart className="w-10 h-10 text-white fill-white" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-6 -left-6 bg-[#2E7D32] text-white rounded-2xl shadow-xl p-4">
                    <p className="font-black text-2xl">€750</p>
                    <p className="text-white/70 text-sm">/{language === 'nl' ? 'maand' : 'month'}</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                    2
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">{language === 'nl' ? 'Stap 2' : 'Step 2'}</p>
                    <h3 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Swipe & Ontdek' : 'Swipe & Discover'}</h3>
                  </div>
                </div>
                <p className="text-lg text-[#9E9E9E] mb-6 leading-relaxed">
                  {language === 'nl' 
                    ? <>Net als een datingsite! Bekijk woningen die bij jou passen. <span className="text-[#2A2A2A] font-bold">Swipe rechts</span> als je interesse hebt, <span className="text-[#2A2A2A] font-bold">links</span> als het niets voor je is.</>
                    : <>Just like a dating site! View properties that match you. <span className="text-[#2A2A2A] font-bold">Swipe right</span> if interested, <span className="text-[#2A2A2A] font-bold">left</span> if not.</>
                  }
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#C04A4A]/10 rounded-xl p-5 text-center">
                    <X className="w-10 h-10 text-[#C04A4A] mx-auto mb-2" />
                    <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Swipe links' : 'Swipe left'}</p>
                    <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Niet interessant' : 'Not interested'}</p>
                  </div>
                  <div className="bg-[#2E7D32]/10 rounded-xl p-5 text-center">
                    <Heart className="w-10 h-10 text-[#2E7D32] mx-auto mb-2" />
                    <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Swipe rechts' : 'Swipe right'}</p>
                    <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Ik wil dit!' : 'I want this!'}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] rounded-xl p-4 text-white">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6" />
                    <p className="font-bold">{language === 'nl' ? '320+ woningen beschikbaar om te swipen' : '320+ properties available to swipe'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center mb-24">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                    3
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">{language === 'nl' ? 'Stap 3' : 'Step 3'}</p>
                    <h3 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Chat & Match' : 'Chat & Match'}</h3>
                  </div>
                </div>
                <p className="text-lg text-[#9E9E9E] mb-6 leading-relaxed">
                  {language === 'nl' 
                    ? <>Bij elke swipe naar rechts wordt de woning opgeslagen in je dashboard. Met <span className="text-[#2A2A2A] font-bold">Premium</span> kun je direct chatten met de verhuurder!</>
                    : <>With every swipe right, the property is saved to your dashboard. With <span className="text-[#2A2A2A] font-bold">Premium</span> you can chat directly with the landlord!</>
                  }
                </p>

                <div className="bg-[#F7F7F7] rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3 mb-4">
                    <MessageSquare className="w-6 h-6 text-[#2E7D32] mt-1" />
                    <div className="flex-1">
                      <p className="font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Direct contact:' : 'Direct contact:'}</p>
                      <div className="bg-white rounded-lg p-4 border border-[#E5E5E5]">
                        <p className="text-[#9E9E9E] italic text-sm">
                          {language === 'nl' 
                            ? '"Hoi! Ik ben geïnteresseerd in deze woning. Graag zou ik een bezichtiging willen plannen. Ik heb een vast inkomen en kan per direct verhuizen!"'
                            : '"Hi! I\'m interested in this property. I\'d love to schedule a viewing. I have a stable income and can move immediately!"'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[#2E7D32]">
                    <Zap className="w-5 h-5" />
                    <span className="font-bold text-sm">{language === 'nl' ? 'Schrijf je eigen bericht aan de verhuurder' : 'Write your own message to the landlord'}</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex -space-x-3">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                        {i}
                      </div>
                    ))}
                  </div>
                  <p className="text-[#9E9E9E] text-sm">
                    <span className="text-[#2A2A2A] font-bold">847 {language === 'nl' ? 'berichten' : 'messages'}</span> {language === 'nl' ? 'verstuurd vandaag' : 'sent today'}
                  </p>
                </div>
              </div>

              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600" 
                    alt={language === 'nl' ? 'Chat en match' : 'Chat and match'} 
                    className="w-full max-w-md rounded-3xl shadow-2xl"
                  />
                  {/* Chat bubble overlay */}
                  <div className="absolute bottom-8 left-8 right-8 bg-white rounded-2xl shadow-xl p-4">
                    <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#E5E5E5]">
                      <div className="w-10 h-10 bg-[#2E7D32] rounded-full flex items-center justify-center text-white font-bold">V</div>
                      <div className="flex-1">
                        <p className="font-bold text-[#2A2A2A] text-sm">{language === 'nl' ? 'Verhuurder Amsterdam' : 'Landlord Amsterdam'}</p>
                        <p className="text-xs text-[#2E7D32]">● {language === 'nl' ? 'Online nu' : 'Online now'}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#2A2A2A]">{language === 'nl' ? '"Super! Kun je zaterdag 14:00 bezichtigen?"' : '"Great! Can you view Saturday at 2pm?"'}</p>
                  </div>
                  <div className="absolute -top-4 -right-4 bg-[#2E7D32] text-white rounded-full px-4 py-2 font-bold shadow-lg animate-bounce">
                    🎉 Match!
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
              <div className="flex justify-center">
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600" 
                    alt={language === 'nl' ? 'Sleuteloverdracht' : 'Key handover'} 
                    className="w-full max-w-md rounded-3xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 bg-[#2E7D32] rounded-xl flex items-center justify-center">
                        <Award className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <p className="font-black text-[#2A2A2A] text-lg">{language === 'nl' ? 'Contract getekend!' : 'Contract signed!'}</p>
                        <p className="text-sm text-[#9E9E9E]">{language === 'nl' ? 'Welkom in je nieuwe thuis 🎊' : 'Welcome to your new home 🎊'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center text-white text-2xl font-black">
                    4
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#2E7D32] uppercase tracking-wide">{language === 'nl' ? 'Stap 4' : 'Step 4'}</p>
                    <h3 className="text-3xl md:text-4xl font-black text-[#2A2A2A]">{language === 'nl' ? 'Verhuizen! 🏠' : 'Move in! 🏠'}</h3>
                  </div>
                </div>
                <p className="text-lg text-[#9E9E9E] mb-6 leading-relaxed">
                  {language === 'nl' 
                    ? <>Plan een bezichtiging, vind je perfecte match en teken het contract. <span className="text-[#2A2A2A] font-bold">82% van onze gebruikers</span> vindt binnen 2 weken een woning.</>
                    : <>Schedule a viewing, find your perfect match and sign the contract. <span className="text-[#2A2A2A] font-bold">82% of our users</span> find a home within 2 weeks.</>
                  }
                </p>

                <div className="bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] rounded-2xl p-6 text-white mb-6">
                  <p className="text-sm text-white/60 mb-4 uppercase tracking-wide font-bold">{language === 'nl' ? 'Onze resultaten' : 'Our results'}</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-3xl md:text-4xl font-black text-[#2E7D32]">82%</p>
                      <p className="text-xs text-white/60">Match rate</p>
                    </div>
                    <div>
                      <p className="text-3xl md:text-4xl font-black text-[#2E7D32]">&lt;2</p>
                      <p className="text-xs text-white/60">{language === 'nl' ? 'Weken gem.' : 'Weeks avg.'}</p>
                    </div>
                    <div>
                      <p className="text-3xl md:text-4xl font-black text-[#2E7D32]">320+</p>
                      <p className="text-xs text-white/60">{language === 'nl' ? 'Woningen' : 'Properties'}</p>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate('/swipematch')}
                  className="w-full bg-[#2E7D32] hover:bg-[#256B2B] text-white h-14 text-lg font-bold rounded-xl"
                >
                  {language === 'nl' ? 'Start nu met swipen' : 'Start swiping now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS - Social Proof */}
        <section className="py-20 px-4 bg-[#F7F7F7]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Star className="w-4 h-4 fill-[#2E7D32]" />
                {language === 'nl' ? '1.200+ TEVREDEN GEBRUIKERS' : '1,200+ HAPPY USERS'}
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-[#2A2A2A]">
                {language === 'nl' ? 'Wat anderen zeggen' : 'What others say'}
              </h2>
            </div>

            {/* Featured testimonial */}
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl mb-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#2E7D32]/5 rounded-full blur-3xl"></div>
              <div className="relative">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <img 
                    src={testimonials[activeTestimonial].image}
                    alt={testimonials[activeTestimonial].name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-[#2E7D32]"
                  />
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-1 mb-4">
                      {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                        <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-4">
                      &ldquo;{testimonials[activeTestimonial].quote}&rdquo;
                    </p>
                    <p className="text-lg text-[#9E9E9E]">
                      <span className="text-[#2A2A2A] font-bold">{testimonials[activeTestimonial].name}</span> • {testimonials[activeTestimonial].role}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      i === activeTestimonial ? 'bg-[#2E7D32] w-8' : 'bg-[#E5E5E5]'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Mini testimonials */}
            <div className="grid md:grid-cols-3 gap-6">
              {(language === 'nl' ? [
                { quote: '"Binnen 5 dagen een bezichtiging!"', name: 'Mark D.', city: 'Den Haag' },
                { quote: '"Nooit meer 100 dezelfde mails sturen"', name: 'Lisa R.', city: 'Eindhoven' },
                { quote: '"Super makkelijk, zelfs mijn moeder snapt het"', name: 'Kevin B.', city: 'Groningen' }
              ] : [
                { quote: '"Got a viewing within 5 days!"', name: 'Mark D.', city: 'The Hague' },
                { quote: '"Never send 100 of the same emails again"', name: 'Lisa R.', city: 'Eindhoven' },
                { quote: '"Super easy, even my mom gets it"', name: 'Kevin B.', city: 'Groningen' }
              ]).map((t, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-lg">
                  <p className="text-lg font-bold text-[#2A2A2A] mb-3">{t.quote}</p>
                  <p className="text-[#9E9E9E] text-sm">{t.name} • {t.city}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA - Ultra Conversion */}
        <section className="relative py-24 md:py-32 px-4 overflow-hidden bg-gradient-to-br from-[#2E7D32] via-[#256B2B] to-[#1F3A5F]">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full">
              <div className="absolute -top-20 -left-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
              <div className="absolute -bottom-20 -right-20 w-[500px] h-[500px] bg-white rounded-full blur-3xl"></div>
            </div>
          </div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Zap className="w-4 h-4" />
              {language === 'nl' ? 'BEPERKTE AANBIEDING' : 'LIMITED OFFER'}
            </div>
            
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              {language === 'nl' ? 'Jouw droomwoning' : 'Your dream home'}<br/>{language === 'nl' ? 'wacht op je' : 'awaits you'}
            </h2>
            
            <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
              <span className="text-white font-bold">{language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swipes'}</span> {language === 'nl' ? 'om te beginnen.' : 'to get started.'} 
              {language === 'nl' ? 'Geen creditcard nodig. Geen verplichtingen.' : 'No credit card needed. No obligations.'}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <Button 
                onClick={() => navigate('/swipematch')}
                className="bg-white text-[#2E7D32] hover:bg-gray-100 h-16 px-12 text-xl font-black rounded-full shadow-2xl hover:shadow-none transition-all group"
              >
                {language === 'nl' ? 'START NU GRATIS' : 'START FREE NOW'}
                <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-white" />
                <span>{language === 'nl' ? 'Gratis te proberen' : 'Free to try'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-white" />
                <span>{language === 'nl' ? '100% veilig & privé' : '100% safe & private'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-white" />
                <span>{language === 'nl' ? '1.200+ gebruikers' : '1,200+ users'}</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HowItWorksPage;
